<template>
	<NcSettingsSection :title="t('holiday_calendars', 'Calendar holidays')"
		:description="t('holiday_calendars', 'Easily subscribe to external calendars like public holidays.')">
		<NcButton type="primary" @click="showModal">
			<template #icon>
				<Plus :size="20" />
			</template>
			{{ t('holiday_calendars', 'Add subscription') }}
		</NcButton>
		<!-- Loading -->
		<div v-show="loading" class="icon icon-loading" />
		<NcEmptyContent v-if="!loading && selectedSubscriptions.length < 1" icon="icon-delete">
			{{ t('holiday_calendars', 'No public holidays calendar subscriptions') }}
		</NcEmptyContent>
		<NcModal v-if="modal" size="large" @close="closeModal">
			<form class="modal__content" @submit.prevent="createNewSubscriptions">
				<h3>{{ t('holiday_calendars', 'Public holidays') }}</h3>
				<div class="NcMultiselect-wrapper">
					<NcMultiselect v-model="holidaySubscriptionToAdd"
						class="country-NcMultiselect"
						:options="availableRemoteHolidayCalendars"
						:placeholder="t('holiday_calendars', 'Pick a country')"
						label="label"
						track-by="trackBy" />
					<NcButton type="primary" native-type="submit">
						{{ t('holiday_calendars', 'Add subscription') }}
					</NcButton>
				</div>
				<h3>{{ t('holiday_calendars', 'Religious calendars') }}</h3>
				<div class="NcMultiselect-wrapper">
					<NcMultiselect v-model="religiousSubscriptionToAdd"
						:options="availableRemoteReligiousCalendars"
						:min-width="400"
						:placeholder="t('holiday_calendars', 'Pick a subscription')"
						label="label"
						track-by="trackBy" />
					<NcButton type="primary" native-type="submit">
						{{ t('holiday_calendars', 'Add subscription') }}
					</NcButton>
				</div>
				<h3>{{ t('holiday_calendars', 'School vacations') }}</h3>
				<div class="NcMultiselect-wrapper">
					<NcMultiselect v-model="schoolHolidaysSubscriptionCountry"
						:options="availableRemoteSchoolHolidaysCalendars"
						:min-width="400"
						:placeholder="t('holiday_calendars', 'Pick a subscription')"
						label="label"
						track-by="trackBy" />
					<NcMultiselect v-if="schoolHolidaysSubscriptionCountry && !schoolHolidaysSubscriptionCountry.url"
						v-model="schoolHolidaysSubscriptionVariant"
						:options="schoolHolidaysVariants"
						:min-width="400"
						:placeholder="t('holiday_calendars', 'Pick a subscription')"
						label="name"
						track-by="url" />
					<NcMultiselect v-if="schoolHolidaysSubscriptionVariant && schoolHolidaysURLs.length > 0"
						v-model="schoolHolidaysSubscriptionURL"
						:options="schoolHolidaysURLs"
						:min-width="400"
						:placeholder="t('holiday_calendars', 'Pick a subscription')"
						label="name"
						track-by="url" />
					<NcButton type="primary" native-type="submit">
						{{ t('holiday_calendars', 'Add subscription') }}
					</NcButton>
				</div>
			</form>
		</NcModal>
		<transition-group name="fade-collapse" tag="ul" class="subscription-list-wrapper">
			<NcListItem v-for="selectedSubscription in selectedSubscriptions"
				:key="selectedSubscription.url"
				:title="selectedSubscription.label || selectedSubscription.name"
				:force-display-actions="true"
				:details="selectedSubscription.datespan">
				<template #subtitle>
					{{ selectedSubscription.authors || selectedSubscription.attribution?.title || selectedSubscription.licence }}
				</template>
				<template #actions>
					<NcActionButton icon="icon-delete" @click="removeSubscription(selectedSubscription)">
						{{ t('Remove') }}
					</NcActionButton>
				</template>
			</NcListItem>
		</transition-group>
		<h3>{{ t('holiday_calendars', 'Sources') }}</h3>
		<div class="attribution">
			<!-- eslint-disable-next-line vue/no-v-html-->
			<p v-html="t('holiday_calendars', 'Public holiday calendar list provided by {thunderbirdLink}').replace('{thunderbirdLink}', thunderbirdLink)" />
			<p>{{ t('holiday_calendars', 'Religious calendar list provided by:') }}</p>
			<ul>
				<li v-for="attribution in religiousCalendarAttributions" :key="attribution.url + attribution.title">
					<a :href="attribution.url" target="_blank" rel="noopener noreferrer">{{ attribution.title }}↗</a>
				</li>
			</ul>
			<p>
				{{ t('holiday_calendars', 'We can\'t allow adding their calendars directly, but you may find some interesting things to subscribe to on the following websites. Once the feed URL copied, you can subscribe to it directly into the Calendar App.') }}
			</p>
			<ul>
				<li v-for="externalWebsite in externalWebsites" :key="externalWebsite.url">
					<a :href="externalWebsite.url" target="_blank" rel="noopener noreferrer">{{ externalWebsite.title }}↗</a>
				</li>
			</ul>
		</div>
	</NcSettingsSection>
</template>

<script>
import { loadState } from '@nextcloud/initial-state'
import NcMultiselect from '@nextcloud/vue/dist/Components/NcMultiselect.js'
import NcListItem from '@nextcloud/vue/dist/Components/NcListItem.js'
import NcActionButton from '@nextcloud/vue/dist/Components/NcActionButton.js'
import NcEmptyContent from '@nextcloud/vue/dist/Components/NcEmptyContent.js'
import NcSettingsSection from '@nextcloud/vue/dist/Components/NcSettingsSection.js'
import NcButton from '@nextcloud/vue/dist/Components/NcButton.js'
import Plus from 'vue-material-design-icons/Plus.vue'
import NcModal from '@nextcloud/vue/dist/Components/NcModal.js'
import usernameToColor from '@nextcloud/vue/dist/Functions/usernameToColor.js'
import {
	createSubscription,
	findAllSubscriptions,
	initializeClientForUserView,
} from './services/caldavService.js'
import { countryNameToFlag } from './services/flagService.js'
import uniqBy from 'lodash/uniqBy.js'

const THUNDERBIRD_ENDPOINT = 'https://www.thunderbird.net/media/caldata/'

export default {
	name: 'PersonalSettings',
	components: {
		NcActionButton,
		NcButton,
		NcEmptyContent,
		NcListItem,
		NcModal,
		NcMultiselect,
		Plus,
		NcSettingsSection,
	},
	data() {
		return {
			appName: 'holiday_calendars',
			thunderbirdLink: '<a href="https://www.thunderbird.net/en-US/calendar/holidays/" target="_blank" rel="noopener noreferrer" hreflang="en-US">thunderbird.net ↗</a>',
			remoteHolidayCalendars: [],
			remoteReligiousCalendars: [],
			remoteSchoolHolidays: [],
			currentSubscriptions: [],
			modal: false,
			loading: true,
			holidaySubscriptionToAdd: null,
			religiousSubscriptionToAdd: null,
			schoolHolidaysSubscriptionCountry: null,
			schoolHolidaysSubscriptionVariant: null,
			schoolHolidaysSubscriptionURL: null,
			externalWebsites: [
				{
					url: 'https://www.calendarlabs.com/ical-calendar/',
					title: 'calendarlabs.com',
				},
				{
					url: 'https://www.busymac.com/webdav.html',
					title: "BusyCal's list",
				},
				{
					url: 'https://www.officeholidays.com/',
					title: 'OfficeHolidays',
				},
				{
					url: 'https://www.thereisadayforthat.com/',
					title: 'There is a Day for That!',
				},
				{
					url: 'http://icalshare.com/',
					title: 'iCalShare',
				},
				{
					url: 'https://www.pogdesign.co.uk/cat/',
					title: 'TV Calendar',
				},
			],
		}
	},
	computed: {
		currentSubscriptionsSources() {
			return this.currentSubscriptions.map(sub => sub.source)
		},
		selectedSubscriptions() {
			return this.currentSubscriptions.map((subscription) => {
				return { ...subscription, ...[...this.remoteHolidayCalendars, ...this.remoteReligiousCalendars, ...this.allSchoolHolidaysVariants].find(({ url }) => url === subscription.source) }
			})
		},
		religiousCalendarAttributions() {
			return uniqBy(this.remoteReligiousCalendars, (calendar) => calendar.attribution.url + calendar.attribution.title).map(({ attribution }) => attribution)
		},
		availableRemoteHolidayCalendars() {
			return this.remoteHolidayCalendars.filter(({ url }) => !this.currentSubscriptionsSources.includes(url)).map(cal => ({ ...cal, label: cal.label + (cal.datespan ? ' (' + cal.datespan + ')' : '') }))
		},
		availableRemoteReligiousCalendars() {
			return this.remoteReligiousCalendars.filter(({ url }) => !this.currentSubscriptionsSources.includes(url))
		},
		availableRemoteSchoolHolidaysCalendars() {
			return this.remoteSchoolHolidays.filter(({ url }) => !this.currentSubscriptionsSources.includes(url))
		},
		schoolHolidaysVariants() {
			if (this.schoolHolidaysSubscriptionCountry !== null) {
				const country = this.remoteSchoolHolidays.find(({ country }) => country === this.schoolHolidaysSubscriptionCountry.country)
				if (country.variants) {
					return country.variants
				}
				return [{ name: country.label, url: country.url }]
			}
			return []
		},
		schoolHolidaysURLs() {
			if (this.schoolHolidaysSubscriptionVariant !== null && this.schoolHolidaysSubscriptionVariant.urls) {
				return this.schoolHolidaysSubscriptionVariant.urls.map(({ date, url }) => ({ name: date, url }))
			}
			return []
		},
		allSchoolHolidaysVariants() {
			return this.remoteSchoolHolidays.reduce((acc, calendar) => {
				if (calendar.variants) {
					calendar.variants.forEach(variant => {
						if (variant.urls) {
							acc.push(...variant.urls.map(({ date, url }) => ({ name: `${calendar.label} - ${variant.name}`, url, datespan: date })))
						} else {
							acc.push({ name: `${calendar.label} - ${variant.name}`, url: variant.url })
						}
					})
				} else {
					acc.push({ name: calendar.label, url: calendar.url })
				}
				return acc
			}, [])
		},
		schoolHolidaysSubscriptionToAdd() {
			if (this.schoolHolidaysSubscriptionCountry.url) return { ...this.schoolHolidaysSubscriptionCountry, name: this.schoolHolidaysSubscriptionCountry.label }
			if (this.schoolHolidaysSubscriptionVariant.url) return this.schoolHolidaysSubscriptionVariant
			return this.schoolHolidaysSubscriptionURL
		},
	},
	async created() {
		this.remoteHolidayCalendars = loadState(this.appName, 'holiday_calendars').map(remoteCalendar => {
			const flag = countryNameToFlag(remoteCalendar.country)
			return { ...remoteCalendar, flag, label: `${flag} ${remoteCalendar.country}`, trackBy: remoteCalendar.filename, url: THUNDERBIRD_ENDPOINT + remoteCalendar.filename }
		})
		this.remoteReligiousCalendars = loadState(this.appName, 'religious_calendars').map(remoteCalendar => {
			return { ...remoteCalendar, label: remoteCalendar.name, trackBy: remoteCalendar.url }
		})
		this.remoteSchoolHolidays = loadState(this.appName, 'school_holidays').map(remoteCalendar => {
			return { ...remoteCalendar, label: remoteCalendar.country, trackBy: remoteCalendar.source }
		})
		await initializeClientForUserView()
		this.currentSubscriptions = await findAllSubscriptions()
		this.loading = false

	},
	methods: {
		showModal() {
			this.modal = true
		},
		closeModal() {
			this.modal = false
		},
		async createNewSubscriptions() {
			this.loading = true
			this.modal = false
			if (this.holidaySubscriptionToAdd) {
				const { country, label, filename } = this.holidaySubscriptionToAdd
				console.debug(this.holidaySubscriptionToAdd)
				await createSubscription(this.t('holiday_calendars', 'Public holidays in {country}', { country }), usernameToColor(label), THUNDERBIRD_ENDPOINT + filename, 0)
			}
			if (this.religiousSubscriptionToAdd) {
				const { label, url } = this.religiousSubscriptionToAdd
				console.debug(this.religiousSubscriptionToAdd)
				await createSubscription(label, usernameToColor(label), url, 0)
			}
			if (this.schoolHolidaysSubscriptionToAdd) {
				const { name, url } = this.schoolHolidaysSubscriptionToAdd
				console.debug(this.schoolHolidaysSubscriptionToAdd)
				await createSubscription(name, usernameToColor(name), url, 0)
			}
			this.holidaySubscriptionToAdd = null
			this.religiousSubscriptionToAdd = null
			this.schoolHolidaysSubscriptionCountry = null
			this.schoolHolidaysSubscriptionVariant = null
			this.schoolHolidaysSubscriptionURL = null
			this.currentSubscriptions = await findAllSubscriptions()
			this.loading = false
		},
		async removeSubscription(calendar) {
			console.debug('remove sub', calendar, this.currentSubscriptions)
			await calendar._request.delete(calendar._url, {})
			this.currentSubscriptions = await findAllSubscriptions()
		},
	},
}
</script>
<style scoped>
.modal__content {
	margin: 1rem;
	min-height: 30rem;
}

.country-NcMultiselect {
	min-width: 300px;
}

:deep(a:not(.list-item)) {
	border: 0;
	color: var(--color-main-text);
	text-decoration: none;
	cursor: pointer;
}

:deep(a:not(.list-item):hover) {
	text-decoration: underline;
}

.attribution p, .attribution ul li {
	opacity: .7;
}

.attribution ul li {
	margin-left: 20px;
	margin-bottom: 10px;
	list-style: circle outside;
}

.NcMultiselect-wrapper {
	display: flex;
	gap: 0.5rem 0.75rem;
	flex-wrap: wrap;
}

.fade-enter-active,
.fade-leave-active,
.fade-collapse-enter-active,
.fade-collapse-leave-active {
	transition: opacity var(--animation-quick), max-height var(--animation-quick);
}

.fade-collapse-enter,
.fade-collapse-leave-to {
	opacity: 0;
	max-height: 0;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

.subscription-list-wrapper {
	max-width: 500px;
}

</style>
